import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode
} from 'react';
// Type-only: socket.io-client se carga con import() dinámico recién al conectar,
// así sus ~110KB no viajan en el chunk eager de contexts ni compiten con el
// primer render en dispositivos lentos.
import type { Socket } from 'socket.io-client';
import { useAuth0 } from '@auth0/auth0-react';
import { hasPerfAccessToken } from '@/auth/perfTokenAuth';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './auth';
import { useClient } from '@/hooks/useClient';
import { createNotificationService, VendorAppNotification } from '@/services/notifications';
import { toast } from '@/components/Toast/toastUtils';
import { invalidateAgendaQueries } from '@/services/agendaQuery';
import { notificationClientName } from '@/utils/clientName';

export interface BookingNotification {
  bookingId: number;
  customerName: string;
  serviceName: string;
  eventDate: string;
  eventTime: string;
  amount: number;
  paymentType: string;
  createdAt: Date;
  type?: 'booking' | 'reschedule' | 'cancellation' | 'refund';
  oldEventDate?: string;
  oldEventTime?: string;
  newEventDate?: string;
  newEventTime?: string;
  read?: boolean;
}

export type ApiNotificationsStatus = 'idle' | 'loading' | 'success' | 'error';

export interface WebSocketContextType {
  isConnected: boolean;
  notifications: BookingNotification[];
  apiNotifications: VendorAppNotification[];
  unreadCount: number;
  apiUnreadCount: number;
  totalUnreadCount: number;
  isLoadingApiNotifications: boolean;
  /** Lifecycle of the first API sync. Screens must not render their empty state
      until it is 'success' (a later failed refresh keeps 'success'). */
  apiNotificationsStatus: ApiNotificationsStatus;
  markAsRead: (index: number) => void;
  markAllAsRead: () => void;
  markApiNotificationAsRead: (notificationId: number) => void;
  markAllApiNotificationsAsRead: () => void;
  archiveApiNotification: (notificationId: number) => void;
  clearNotifications: () => void;
  removeRealtimeNotification: (index: number) => void;
  removeOldNotifications: (daysOld?: number) => void;
  syncApiNotifications: () => Promise<void>;
  /** Subscribe to a Socket.io event from feature code without leaking the raw
      socket. Returns an unsubscribe function. Handler is auto-rebound across
      reconnects so callers don't have to care about socket lifecycle. */
  subscribe: <T = unknown>(event: string, handler: (payload: T) => void) => () => void;
}

export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  children: ReactNode;
}

const MAX_NOTIFICATIONS = 50; // Maximum notifications to keep in memory

// Browser notification with graceful degradation. In iOS Safari outside an
// installed PWA, registration.showNotification doesn't exist even with
// Notification.permission === 'granted', so fall back to new Notification().
export const showBrowserNotification = (title: string, body: string, onClick?: () => void) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (typeof window !== 'undefined' && !window.location.protocol.startsWith('https')) {
    console.warn('Notifications require HTTPS context');
    return;
  }
  const fallback = () => {
    try {
      new Notification(title, { body, icon: '/logo-192.png' });
    } catch (error) {
      console.error('Failed to create notification:', error);
    }
  };
  // Con destino al tocarla: la del service worker no avisa el clic, así que se usa la de la página.
  if (onClick) {
    try {
      const n = new Notification(title, { body, icon: '/logo-192.png' });
      n.onclick = () => {
        window.focus();
        onClick();
        n.close();
      };
      return;
    } catch {
      // iOS fuera de la app instalada: sigue por el service worker, sin destino
    }
  }
  if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
    navigator.serviceWorker.ready
      .then((registration) => {
        if (typeof registration.showNotification === 'function') {
          registration.showNotification(title, { body, icon: '/logo-192.png' });
        } else {
          fallback();
        }
      })
      .catch(fallback);
  } else {
    fallback();
  }
};
const LEGACY_STORAGE_KEY = 'websocket_notifications';

// Solo alimenta la clave de dedupe entre notificaciones realtime y de la API. Las
// reseñas no son reservas: sin su rama caían en 'booking' y podían pisar la clave
// de una reserva real del mismo cliente.
const mapVendorNotificationType = (
  type: VendorAppNotification['type']
): BookingNotification['type'] | 'review' | 'attendance' | 'premio' => {
  switch (type) {
    // Avisos del fichaje: no son reservas, nunca deben deduplicarse contra una en tiempo real.
    case 'attendance_missing':
    case 'attendance_pin_locked':
      return 'attendance';
    // Premios: tampoco son reservas (el de mañana trae la reserva, pero no es la misma noticia).
    case 'premio_won':
    case 'premio_used':
    case 'premio_tomorrow':
      return 'premio';
    case 'booking_rescheduled':
      return 'reschedule';
    case 'booking_cancelled':
      return 'cancellation';
    case 'booking_refunded':
      return 'refund';
    // La del profesional (review_for_professional) trae el nombre del cliente y el turno: sin su
    // rama se leía como una reserva.
    case 'review_received':
    case 'review_private_note':
    case 'google_review_received':
    case 'review_for_professional':
      return 'review';
    case 'booking_confirmed':
    default:
      return 'booking';
  }
};

const normalizeStringValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? '' : value.toISOString();
  }
  return String(value);
};

const normalizeDateValue = (value?: string | Date): string => {
  if (!value) {
    return '';
  }
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? '' : value.toISOString();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
};

const buildRealtimeNotificationKey = (notification: BookingNotification): string => {
  return [
    normalizeStringValue(notification.type ?? 'booking'),
    normalizeStringValue(notification.bookingId),
    normalizeStringValue(notification.customerName),
    normalizeStringValue(notification.serviceName),
    normalizeStringValue(notification.eventDate),
    normalizeStringValue(notification.eventTime),
    normalizeStringValue(notification.oldEventDate),
    normalizeStringValue(notification.oldEventTime),
    normalizeStringValue(notification.newEventDate),
    normalizeStringValue(notification.newEventTime),
    normalizeDateValue(notification.createdAt)
  ].join('|');
};

const buildApiNotificationKey = (notification: VendorAppNotification): string => {
  const { data } = notification;
  return [
    normalizeStringValue(mapVendorNotificationType(notification.type)),
    normalizeStringValue(notification.booking_id ?? data?.bookingId),
    normalizeStringValue(data?.customerName),
    normalizeStringValue(data?.serviceName),
    normalizeStringValue(data?.eventDate),
    normalizeStringValue(data?.eventTime),
    normalizeStringValue(data?.oldEventDate),
    normalizeStringValue(data?.oldEventTime),
    normalizeStringValue(data?.newEventDate),
    normalizeStringValue(data?.newEventTime),
    normalizeDateValue(data?.createdAt ?? notification.created_at)
  ].join('|');
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { session } = useAuth();
  const storageKey =
    session?.id && session?.vendor?.id
      ? `${LEGACY_STORAGE_KEY}:v2:${session.id}:${session.vendor.id}:${session.is_staff ? 'staff' : 'owner'}`
      : null;
  // Reset memory and subscriptions before rendering a different account's children.
  return (
    <ScopedWebSocketProvider key={storageKey ?? 'signed-out'} storageKey={storageKey}>
      {children}
    </ScopedWebSocketProvider>
  );
};

const ScopedWebSocketProvider: React.FC<WebSocketProviderProps & { storageKey: string | null }> = ({
  children,
  storageKey
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { session } = useAuth();
  // La sesión por enlace de autologin (`?ott`, webview de Instagram, directorio) llega
  // con un Bearer propio y sin sesión de Auth0: la API responde igual, así que las
  // novedades se sincronizan también en ese modo (el socket sigue atado a Auth0).
  const canCallApi = isAuthenticated || hasPerfAccessToken();
  const { client } = useClient();
  const queryClient = useQueryClient();
  const notificationService = useMemo(() => createNotificationService(client), [client]);

  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);
  socketRef.current = socket;
  /** Active feature subscribers, keyed by event name. The set survives socket
      reconnects because we re-bind every handler whenever a new socket comes up. */
  const subscribersRef = useRef<Map<string, Set<(payload: any) => void>>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<BookingNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnecting, setIsConnecting] = useState(false);

  // API notifications state
  const [apiNotifications, setApiNotifications] = useState<VendorAppNotification[]>([]);
  const [apiUnreadCount, setApiUnreadCount] = useState(0);
  const [isLoadingApiNotifications, setIsLoadingApiNotifications] = useState(false);
  const [apiNotificationsStatus, setApiNotificationsStatus] = useState<ApiNotificationsStatus>('idle');
  const archivedNotificationIds = useRef(new Set<number>());
  const archivingNotificationIds = useRef(new Set<number>());
  const scopeActive = useRef(true);
  useEffect(() => {
    scopeActive.current = true;
    return () => {
      scopeActive.current = false;
    };
  }, []);

  const [cacheHydrated, setCacheHydrated] = useState(false);
  // Legacy rows carry no owner identity, so they cannot safely be migrated.
  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      if (!storageKey) return;
      const savedNotifications = localStorage.getItem(storageKey);
      if (!savedNotifications) return;
      const parsed = JSON.parse(savedNotifications);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 30);
      const fresh: BookingNotification[] = (
        Array.isArray(parsed.notifications) ? parsed.notifications : []
      ).filter((n: BookingNotification) => n && new Date(n.createdAt) > cutoffDate);
      setNotifications(fresh);
      setUnreadCount(fresh.filter((n) => !n.read).length);
    } catch (error) {
      console.error('Error loading saved notifications:', error);
    } finally {
      setCacheHydrated(true);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || !cacheHydrated) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          notifications: notifications.slice(0, MAX_NOTIFICATIONS),
          unreadCount,
          lastUpdated: new Date().toISOString()
        })
      );
    } catch (error) {
      console.error('Error saving notifications:', error);
    }
  }, [notifications, unreadCount, storageKey, cacheHydrated]);

  // Sync API notifications function
  const isOwner = !session?.is_staff;
  const syncApiNotifications = useCallback(async (): Promise<void> => {
    if (!canCallApi || !session?.vendor?.id) return;

    setIsLoadingApiNotifications(true);
    setApiNotificationsStatus((prev) => (prev === 'success' ? prev : 'loading'));
    try {
      const response = await notificationService.fetchNotifications(1, 50);
      // A GET started before an archive can return after its successful PUT.
      const snapshot = response.data.notifications;
      const fetchedNotifications = snapshot.filter((n) => !archivedNotificationIds.current.has(n.id));
      const archivedUnread = snapshot.filter(
        (n) => archivedNotificationIds.current.has(n.id) && !n.read
      ).length;
      setApiNotifications(fetchedNotifications);
      setApiUnreadCount(Math.max(0, response.data.pagination.unreadCount - archivedUnread));
      setApiNotificationsStatus('success');

      if (fetchedNotifications.length > 0) {
        const apiNotificationKeys = new Set(
          fetchedNotifications.map((notification) => buildApiNotificationKey(notification))
        );
        // Para el dueño, la fila de la base se escribe ANTES de emitir el socket: un aviso en
        // tiempo real más viejo que la página de la API ya está guardado (acá con otra clave, o
        // más abajo, en una página que no se pide). Sin este corte, en un negocio con más de 50
        // avisos nuevos uno de hace semanas quedaba en el navegador hasta 30 días y se dibujaba
        // arriba de todo. El profesional del equipo no tiene fila propia: a él solo lo depura
        // la clave y el corte de 30 días.
        const oldestFetchedAt = isOwner
          ? Math.min(
              ...fetchedNotifications
                .map((notification) => new Date(notification.created_at).getTime())
                .filter((time) => !Number.isNaN(time))
            )
          : Number.NEGATIVE_INFINITY;
        setNotifications((prev) => {
          const kept = prev.filter((notification) => {
            const realtimeKey = buildRealtimeNotificationKey(notification);
            if (realtimeKey && apiNotificationKeys.has(realtimeKey)) return false;
            const at = new Date(notification.createdAt).getTime();
            return Number.isNaN(at) || at > oldestFetchedAt;
          });
          const keptSet = new Set(kept);
          const droppedUnread = prev.filter((n) => !n.read && !keptSet.has(n)).length;
          if (droppedUnread > 0) setUnreadCount((count) => Math.max(0, count - droppedUnread));
          return kept;
        });
      }
    } catch (error) {
      console.error('Failed to sync API notifications:', error);
      setApiNotificationsStatus((prev) => (prev === 'success' ? prev : 'error'));
    } finally {
      setIsLoadingApiNotifications(false);
    }
  }, [canCallApi, session?.vendor?.id, isOwner, notificationService]);

  useEffect(() => {
    let currentSocket: Socket | null = null;
    let cancelled = false;

    if (!isAuthenticated || !session?.vendor?.id) {
      // Clean up any existing socket when not authenticated
      if (socket) {
        console.log('Cleaning up socket due to auth/vendor change');
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // If socket already exists and is connected, don't create a new one
    if (socket && socket.connected) {
      console.log('Socket already connected, skipping connection');
      return;
    }

    // Prevent multiple simultaneous connections
    if (isConnecting) {
      console.log('Connection already in progress, skipping');
      return;
    }

    // Clean up any existing socket before creating a new one
    if (socket) {
      console.log('Cleaning up existing socket before reconnection');
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }

    const connectWebSocket = async () => {
      try {
        setIsConnecting(true);
        console.log('Attempting to connect to WebSocket...');
        const [{ io }, token] = await Promise.all([import('socket.io-client'), getAccessTokenSilently()]);

        // The effect may have re-run (vendor/auth switch) while the import +
        // token fetch were in flight — connecting now would wire a socket for
        // the STALE session and race the new effect's socket in React state.
        if (cancelled) {
          setIsConnecting(false);
          return;
        }

        currentSocket = io(
          import.meta.env.VITE_CYCLONE_API_URL?.replace('/v1', '') || 'http://localhost:4000',
          {
            auth: {
              token
            },
            transports: ['websocket', 'polling'],
            forceNew: true, // Force a new connection
            timeout: 5000 // Add connection timeout
          }
        );

        currentSocket.on('connect', () => {
          console.log('Connected to WebSocket server');
          setIsConnected(true);
          setIsConnecting(false);
          // Sync API notifications when WebSocket connects
          syncApiNotifications();
        });

        currentSocket.on('disconnect', (reason) => {
          console.log('Disconnected from WebSocket server:', reason);
          setIsConnected(false);
          setIsConnecting(false);
        });

        currentSocket.on('connect_error', (error) => {
          console.error('WebSocket connection error:', error);
          setIsConnected(false);
          setIsConnecting(false);
        });

        currentSocket.on('room_joined', (data) => {
          console.log('Successfully joined vendor room:', data);
          // Catch up on bookings missed while disconnected (or before the first
          // connection). Wait until the room is joined so new events cannot slip
          // between the snapshot and the subscription.
          void invalidateAgendaQueries(queryClient);
          // Lo mismo con Conversaciones: lista, conversación abierta y contador del menú.
          void queryClient.invalidateQueries({
            predicate: (q) =>
              q.queryKey[0] === 'conversations' &&
              (q.queryKey[1] === 'list' || q.queryKey[1] === 'detail' || q.queryKey[1] === 'summary')
          });
        });

        currentSocket.on('booking:confirmed', (data: any) => {
          console.log('New booking notification:', data);

          const bookingNotification: BookingNotification = {
            ...data,
            type: 'booking',
            read: false
          };

          setNotifications((prev) => {
            // Add new notification at the beginning and limit total count
            const newNotifications = [bookingNotification, ...prev].slice(0, MAX_NOTIFICATIONS);
            return newNotifications;
          });

          setUnreadCount((prev) => prev + 1);

          // Auto-refresh agenda queries
          void invalidateAgendaQueries(queryClient);

          // In-app toast notification
          toast.success(`${notificationClientName(data.customerName)} reservó ${data.serviceName}`, {
            description: `${data.eventDate} a las ${data.eventTime}`,
            duration: 8000
          });

          // Dispatch event for calendar highlight
          window.dispatchEvent(new CustomEvent('booking:new', { detail: { bookingId: data.bookingId } }));

          // Optional: Show browser notification if permission is granted and supported
          showBrowserNotification(
            'Nueva Reserva',
            `${notificationClientName(data.customerName)} reservó ${data.serviceName}`
          );
        });

        currentSocket.on('booking:rescheduled', (data: any) => {
          console.log('New reschedule notification:', data);

          const rescheduleNotification: BookingNotification = {
            ...data,
            type: 'reschedule',
            read: false,
            eventDate: data.newEventDate,
            eventTime: data.newEventTime
          };

          setNotifications((prev) => {
            // Add new reschedule notification at the beginning and limit total count
            const newNotifications = [rescheduleNotification, ...prev].slice(0, MAX_NOTIFICATIONS);
            return newNotifications;
          });

          setUnreadCount((prev) => prev + 1);

          // Auto-refresh agenda queries
          void invalidateAgendaQueries(queryClient);

          // In-app toast notification
          toast.info(`${notificationClientName(data.customerName)} reagendó ${data.serviceName}`, {
            description: `Nueva fecha: ${data.newEventDate} a las ${data.newEventTime}`,
            duration: 8000
          });

          // Optional: Show browser notification if permission is granted and supported
          showBrowserNotification(
            'Reagendamiento',
            `${notificationClientName(data.customerName)} reagendó ${data.serviceName} para ${data.newEventDate} ${data.newEventTime}`
          );
        });

        currentSocket.on('booking:cancelled', (data: any) => {
          console.log('New cancellation notification:', data);

          const cancellationNotification: BookingNotification = {
            ...data,
            type: 'cancellation',
            read: false
          };

          setNotifications((prev) => {
            // Add new cancellation notification at the beginning and limit total count
            const newNotifications = [cancellationNotification, ...prev].slice(0, MAX_NOTIFICATIONS);
            return newNotifications;
          });

          setUnreadCount((prev) => prev + 1);

          // Auto-refresh agenda queries
          void invalidateAgendaQueries(queryClient);

          // In-app toast notification
          toast.error(`${notificationClientName(data.customerName)} canceló ${data.serviceName}`, {
            description: `${data.eventDate} a las ${data.eventTime}`,
            duration: 8000
          });

          // Optional: Show browser notification if permission is granted and supported
          showBrowserNotification(
            'Cancelación',
            `${notificationClientName(data.customerName)} canceló ${data.serviceName}`
          );
        });

        currentSocket.on('booking:refunded', (data: any) => {
          console.log('New refund notification:', data);

          const refundNotification: BookingNotification = {
            ...data,
            type: 'refund',
            read: false
          };

          setNotifications((prev) => {
            // Add new refund notification at the beginning and limit total count
            const newNotifications = [refundNotification, ...prev].slice(0, MAX_NOTIFICATIONS);
            return newNotifications;
          });

          setUnreadCount((prev) => prev + 1);

          // Auto-refresh agenda queries
          void invalidateAgendaQueries(queryClient);

          // In-app toast notification
          toast.warning(`${notificationClientName(data.customerName)} solicitó reembolso`, {
            description: `${data.serviceName} — ${data.eventDate}`,
            duration: 8000
          });

          // Optional: Show browser notification if permission is granted and supported
          showBrowserNotification(
            'Reembolso',
            `${notificationClientName(data.customerName)} solicitó reembolso de ${data.serviceName}`
          );
        });

        setSocket(currentSocket);
      } catch (error) {
        console.error('Error connecting to WebSocket:', error);
        setIsConnected(false);
        setIsConnecting(false);
      }
    };

    // Defer the socket boot to idle: the token fetch + socket.io download used to
    // fire the instant auth resolved, competing with the first render on slow
    // devices. Realtime notifications can wait a few seconds.
    const hasIdle = typeof window.requestIdleCallback === 'function';
    const idleId = hasIdle
      ? window.requestIdleCallback(() => connectWebSocket(), { timeout: 4000 })
      : window.setTimeout(connectWebSocket, 3000);

    return () => {
      console.log('WebSocket effect cleanup triggered');
      cancelled = true;
      if (hasIdle) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
      setIsConnecting(false);
      if (currentSocket) {
        console.log('Disconnecting current socket in cleanup');
        currentSocket.disconnect();
        currentSocket = null;
      }
      // Also clean up the state socket if it exists
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, session?.vendor?.id]);

  // Request notification permission after the app settles (not at mount: the
  // browser prompt + permission work competed with first paint on slow devices).
  useEffect(() => {
    const request = () => {
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        if (typeof Notification.requestPermission === 'function') {
          Notification.requestPermission().catch((error) => {
            console.warn('Failed to request notification permission:', error);
          });
        } else {
          console.warn('Notification.requestPermission is not available');
        }
      }
    };
    const hasIdle = typeof window.requestIdleCallback === 'function';
    const idleId = hasIdle
      ? window.requestIdleCallback(request, { timeout: 8000 })
      : window.setTimeout(request, 5000);
    return () => {
      if (hasIdle) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, []);

  // Sync API notifications when authentication/vendor changes
  useEffect(() => {
    if (canCallApi && session?.vendor?.id) {
      syncApiNotifications();
    }
  }, [canCallApi, session?.vendor?.id]);

  const markAsRead = useCallback((index: number) => {
    setNotifications((prev) =>
      prev.map((notification, i) => (i === index ? { ...notification, read: true } : notification))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    if (storageKey) localStorage.removeItem(storageKey);
  }, [storageKey]);

  const removeOldNotifications = useCallback((daysOld = 7) => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    setNotifications((prev) => {
      const removedUnread = prev.filter((n) => !n.read && new Date(n.createdAt) <= cutoffDate).length;
      if (removedUnread > 0) {
        setUnreadCount((c) => Math.max(0, c - removedUnread));
      }
      return prev.filter((notification) => new Date(notification.createdAt) > cutoffDate);
    });
  }, []);

  const removeRealtimeNotification = useCallback((index: number) => {
    setNotifications((prev) => {
      const target = prev[index];
      if (!target) return prev;
      if (!target.read) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const markApiNotificationAsRead = useCallback(
    async (notificationId: number) => {
      try {
        await notificationService.markAsRead(notificationId);
        setApiNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId ? { ...notification, read: true } : notification
          )
        );
        setApiUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark API notification as read:', error);
      }
    },
    [notificationService]
  );

  const markAllApiNotificationsAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setApiNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
      setApiUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all API notifications as read:', error);
    }
  }, [notificationService]);

  const archiveApiNotification = useCallback(
    async (notificationId: number) => {
      if (
        archivingNotificationIds.current.has(notificationId) ||
        archivedNotificationIds.current.has(notificationId)
      )
        return;
      archivingNotificationIds.current.add(notificationId);
      try {
        await notificationService.archiveNotification(notificationId);
        if (!scopeActive.current) return;
        archivedNotificationIds.current.add(notificationId);
        // Remove from local state
        setApiNotifications((prev) => {
          const notificationToArchive = prev.find((n) => n.id === notificationId);
          // Recalculate unread count if the archived notification was unread
          if (notificationToArchive && !notificationToArchive.read) {
            setApiUnreadCount((c) => Math.max(0, c - 1));
          }
          return prev.filter((n) => n.id !== notificationId);
        });
      } catch (error) {
        console.error('Failed to archive API notification:', error);
        if (scopeActive.current) toast.error('No pudimos archivar la notificación. Intentá de nuevo.');
      } finally {
        archivingNotificationIds.current.delete(notificationId);
      }
    },
    [notificationService]
  );

  const totalUnreadCount = unreadCount + apiUnreadCount;

  // Stable subscribe — each call adds the handler to a Set keyed by event name,
  // and binds it on the current socket. Returns an unsubscribe that removes both.
  const subscribe = useCallback(<T,>(event: string, handler: (payload: T) => void) => {
    let set = subscribersRef.current.get(event);
    if (!set) {
      set = new Set();
      subscribersRef.current.set(event, set);
    }
    set.add(handler as any);
    socketRef.current?.on(event, handler as any);
    return () => {
      set!.delete(handler as any);
      socketRef.current?.off(event, handler as any);
    };
  }, []);

  // Re-bind every active subscriber when the socket itself changes (reconnect).
  useEffect(() => {
    if (!socket) return;
    const cleanups: Array<() => void> = [];
    subscribersRef.current.forEach((handlers, event) => {
      handlers.forEach((handler) => {
        socket.on(event, handler);
        cleanups.push(() => socket.off(event, handler));
      });
    });
    return () => {
      cleanups.forEach((c) => c());
    };
  }, [socket]);

  const value: WebSocketContextType = useMemo(
    () => ({
      isConnected,
      notifications,
      apiNotifications,
      unreadCount,
      apiUnreadCount,
      totalUnreadCount,
      isLoadingApiNotifications,
      apiNotificationsStatus,
      markAsRead,
      markAllAsRead,
      markApiNotificationAsRead,
      markAllApiNotificationsAsRead,
      archiveApiNotification,
      clearNotifications,
      removeRealtimeNotification,
      removeOldNotifications,
      syncApiNotifications,
      subscribe
    }),
    [
      isConnected,
      notifications,
      apiNotifications,
      unreadCount,
      apiUnreadCount,
      totalUnreadCount,
      isLoadingApiNotifications,
      apiNotificationsStatus,
      markAsRead,
      markAllAsRead,
      markApiNotificationAsRead,
      markAllApiNotificationsAsRead,
      archiveApiNotification,
      clearNotifications,
      removeRealtimeNotification,
      removeOldNotifications,
      syncApiNotifications,
      subscribe
    ]
  );

  return <WebSocketContext value={value}>{children}</WebSocketContext>;
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};
